import { isConvertibleToNumber } from "~/utils/isConvertibleToNumber";

interface FormatCurrencyOptions {
  currency?: string;
  currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
  locale?: string;
  precision?: number;
  showPositivePrefix?: boolean;
}

/**
 * Function to format a number as a currency
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
 *
 * @param {number|string} value - value to be formatted
 * @param {FormatCurrencyOptions} options - formatting options
 * @returns {string | undefined} - value formatted as currency
 */
export const formatCurrency = (
  value: number | string,
  options: FormatCurrencyOptions = {},
): string | undefined => {
  if (!isConvertibleToNumber(value)) {
    return undefined;
  }

  const currencyValue = Number(value);
  const {
    currency = "EUR",
    currencyDisplay = "symbol",
    locale = "de-DE",
    precision = 2,
    showPositivePrefix = false,
  } = options;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  return `${showPositivePrefix && currencyValue > 0 ? "+" : ""}${formatter.format(currencyValue)}`;
};
